import React, { useState, useEffect, useCallback } from 'react'
import { useLocation, useNavigate } from 'react-router-dom'
import OtpInput from 'react-otp-input'
import {
  forgotPassword,
  submitForgotPassword,
} from '../../services/AuthenticationService'
import { Container, Box, Button, Typography } from '../shared/MaterialExports'
import { MpTypography } from '../shared'
import PasswordField from '../shared/atom/PasswordField'
import ValidationHint from '../shared/atom/MpValidationHint'
import AnalyticsManager from '../../services/AnalyticsManager'
import { useAppDispatch } from '../../app/hooks'
import { AuthLoader } from '../gateways/AuthLoader'
import { signInWithEmail } from '@meprism/shared/src/redux/authentication/authenticationSlice'
import useMediaQuery from '@mui/material/useMediaQuery'
import { theme, fonts } from '../../theme/OnboardingTheme'
import Loader from '@meprism/shared/src/components/atoms/Loader'

export interface ChangePasswordRouteParam {
  username: string
  from?: Location
}

const styles = {
  button: {
    background: '#1A1B20',
    border: '2px solid #C67EFF',
    fontFamily: fonts.Inter.SemiBold,
    color: '#C67EFF',
    minWidth: '180px',
    width: '100%',
    '&.Mui-disabled': {
      background: '#426BF0',
      color: '#C67EFF',
    },
    ':hover': {
      background: '#1A1B20',
      color: '#C67EFF',
    },
  },
};

const minPasswordLength = 10

const ChangePassword = () => {
  const { state } = useLocation()
  const from = (state as ChangePasswordRouteParam)?.from || '/'
  const { username } = state as ChangePasswordRouteParam
  const navigate = useNavigate()
  const dispatch = useAppDispatch()
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'))

  // OTP States
  const [otp, setOtp] = useState<string>('')
  const [otpErrorText, setOtpErrorText] = useState<string>('')

  // Password States
  const [password, setPassword] = useState<string>('')
  const [confirmPassword, setConfirmPassword] = useState<string>('')
  const [shouldDisplayPasswordInfo, setShouldDisplayPasswordInfo] = useState<boolean>(false)
  const [shouldShowMatchingPasswords, setShouldShowMatchingPasswords] = useState<boolean>(false)
  const [hasSufficientLength, setHasSufficientLength] = useState<boolean>(false)
  const [hasUpperCase, setHasUpperCase] = useState<boolean>(false)
  const [hasSymbol, setHasSymbol] = useState<boolean>(false)
  const [hasMatchingPassword, setHasMatchingPassword] = useState<boolean>(true)
  const [passwordHistoryError, setPasswordHistoryError] = useState<string>('')
  const [loading, setLoading] = useState<boolean>(false)

  const isPasswordValid = hasSufficientLength && hasUpperCase && hasSymbol && hasMatchingPassword
  const isFormValid = isPasswordValid && otp.length > 0

  useEffect(() => {
    setHasSufficientLength(password.length >= minPasswordLength)
    setHasUpperCase(new RegExp(/[A-Z]/).test(password))
    setHasSymbol(new RegExp(/[^A-Za-z0-9]/).test(password))
    setHasMatchingPassword(password === confirmPassword)
    setPasswordHistoryError('')
  }, [password, confirmPassword])

  const onPasswordReset = async () => {
    setLoading(true)
    try {
      await submitForgotPassword(username, otp, password)
      await dispatch(
        signInWithEmail({ username, password } as {
          username: string
          password: string
        })
      ).unwrap()
      await AnalyticsManager.registerSuperProperties(
        {"email": username}
      )
      await AnalyticsManager.trackTypedEvent(
        {
          event: 'Sign In - Web',
          source: "Web App"
        }
      )
      navigate('/changePasswordSuccess', { state: from })
    } catch (error) {
      setLoading(false)
      if (error instanceof Error) {
        switch (error.name) {
          case 'CodeMismatchException':
            setOtpErrorText('The code you have entered is incorrect. Please try again')
            break
          case 'ExpiredCodeException':
            await forgotPassword(username)
            setOtpErrorText(`The code you entered has expired. We have sent a new one to ${username}`)
            break
          case 'LimitExceededException':
            setOtpErrorText(
              `Unfortunately, you have exceeded a limit on the number of failed login attempts. Please wait 15 minutes and try again.`
            )
            AnalyticsManager.error('CognitoLimit', error)
            break
          case 'PasswordHistoryPolicyViolationException':
            setPasswordHistoryError('You cannot reuse a previous password. Please choose a different password.')
            setShouldDisplayPasswordInfo(true)
            break
          default:
            setOtpErrorText('An error occurred processing your request. Please try again later.')
            AnalyticsManager.error(`Unhandled Authorization error: `, error)
            break
        }
      } else {
        AnalyticsManager.error(`Unhandled Authorization error: `, error)
      }
    }
  }

  const handleResendCode = useCallback(
    async (callback: (message: string) => void) => {
      try {
        await forgotPassword(username);
        callback('A new verification code has been sent.');
      } catch (error) {
        callback('Failed to resend code. Please try again later.');
      }
    },
    [username]
  );



  return (
    <AuthLoader>
      <Container fixed maxWidth={'sm'}>
        <Box
          mt="10px"
          display="flex"
          flexDirection="column"
          alignItems="left"
          justifyContent="left">
          <Typography variant={'h2'} sx={{ marginTop: '50px', marginBottom: '10px', textAlign: 'center' }}>
            Reset Your Password
          </Typography>

          {/* OTP Section */}
          <MpTypography mt="40px" mb="20px" variant="subheader" textAlign="center">
            {`Enter the verification code sent to ${username}`}
          </MpTypography>
          <OtpInput
            value={otp}
            onChange={(value) => {
              const numericValue = value.replace(/\D/g, '');
              setOtp(numericValue);
            }}
            inputStyle={{
              width: isMobile ? '16.666%' : '25%',
              height: isMobile ? '42px' : '60px',
              border: '2px solid #EFEDFDB2',
              borderRadius: '10px',
              background: '#1A1B20',
              color: '#ffffff',
            }}
            numInputs={6}
            renderSeparator={<span>-</span>}
            renderInput={(props) => 
            <input 
            {...props} 
            inputMode="numeric"
            pattern="[0-9]*"
            onBeforeInput={(event: React.FormEvent<HTMLInputElement>) => {
                const e = event.nativeEvent as InputEvent;
                if (e.data && !/^[0-9]$/.test(e.data)) {
                  event.preventDefault();
                }
              }}
            />}
            onPaste={(e) => {
              e.preventDefault();
              const pastedText = e.clipboardData.getData('text');
              const digitsOnly = pastedText.replace(/\D/g, '');
              if (!digitsOnly) return;
              setOtp(digitsOnly.slice(0, 6));
            }}
          />
          {isMobile ? (
            <Box sx={{ minHeight: otpErrorText ? 100 : 30 , mt: 3 }}>
              <MpTypography color={'error'}>{otpErrorText}</MpTypography>
            </Box>
          ) : (
            <Box sx={{ minHeight: 60 , mt: 3  }}>
              <MpTypography color={'error'}>{otpErrorText}</MpTypography>
            </Box>
          )}
          <PasswordField
            value={password}
            onChange={(event) => setPassword(event.target.value.replace(/\s/g, ''))}
            onFocus={() => setShouldDisplayPasswordInfo(true)}
            placeholder={'Password'}
            variant={'outlined'}
            sx={{
              width: '100%', 
              margin: 1,
              "& input:-webkit-autofill": {
                WebkitBoxShadow: "0 0 0 1000px #1A1B20 inset !important",
                transition: "background-color 5000s ease-in-out 0s",
              },
            }}
            autoComplete="new-password"
          />
          <PasswordField
            value={confirmPassword}
            onChange={(event) => setConfirmPassword(event.target.value.replace(/\s/g, ''))}
            onFocus={() => setShouldShowMatchingPasswords(true)}
            placeholder="Confirm Password"
            variant={'outlined'}
            sx={{
              width: '100%', 
              margin: 1,
              "& input:-webkit-autofill": {
                WebkitBoxShadow: "0 0 0 1000px #1A1B20 inset !important",
                transition: "background-color 5000s ease-in-out 0s",
              },
            }}
            autoComplete="new-password"
          />
          <Box sx={{ minHeight: shouldDisplayPasswordInfo ? 120 : 50 }}>
            {shouldDisplayPasswordInfo && (
              <Box>
                {passwordHistoryError && (
                  <ValidationHint
                    isValid={false}
                    text={passwordHistoryError}
                  />
                )}
                <ValidationHint
                  isValid={hasSufficientLength}
                  text={`At least ${minPasswordLength} characters`}
                />
                <ValidationHint
                  isValid={hasUpperCase}
                  text={'Contains at least one upper case letter'}
                />
                <ValidationHint isValid={hasSymbol} text={'Contains number or symbol'} />
                {shouldShowMatchingPasswords && (
                  <ValidationHint isValid={hasMatchingPassword} text={'Passwords match'} />
                )}
              </Box>
            )}
          </Box>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              mt: 1,
              mb: 2,
              flexDirection: ['column', 'row'],
              width: '100%',
            }}>
            <Button
              variant={'contained'}
              disabled={!isFormValid || loading || otp.length < 6}
              sx={{
                minWidth: '180px',
                width: '100%',
                mb: [2, 0],
                mr: [0, '10px'],
              }}
              onClick={onPasswordReset}>
              <Box 
                sx={{
                  display: 'flex',   
                  alignItems: 'center',
                  gap: 2,
                  justifyContent: 'center',
                  whiteSpace: 'nowrap',
                  width: '38%',
                  '& .MuiCircularProgress-root': {
                    color: '#5856d6 !important',
                    width: '20px !important',
                    height: '20px !important',
                  },
                  }}
                >
                <Box sx={{ flexGrow: 1, textAlign: 'center' }}>
                  Reset Password
                </Box>
                <Loader loading={loading} />
              </Box>
            </Button>
            <Button
              onClick={() =>
                handleResendCode((message) => {
                  setOtpErrorText(message)
                })
              }
              sx={styles.button}
            >
            Resend Code
            </Button>
          </Box>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              width: '100%',
              mt: 2,
            }}
          >
            <Button
              onClick={() => navigate(-1)}
              sx={styles.button}
            >
              Change Email
            </Button>
          </Box>
        </Box>
      </Container>
    </AuthLoader>
  )
}

export default ChangePassword